import { CREATURE_TYPES, type CreatureType } from "./creatures.ts";
import type { Difficulty, Mode } from "./match.ts";

/** Curva quadrática m(d) = a + b·d + c·d² (doc 01, Escalada). */
export type Curve = readonly [number, number, number];
export const curve = ([a, b, c]: Curve, day: number): number => a + b * day + c * day * day;

export interface DifficultyRules {
  /** Dia e noite, em segundos (doc 01, Ciclo dia/noite). */
  day: number;
  night: number;
  /** Multiplicadores de vida e de dano das assombrações pelo dia. */
  hp: Curve;
  damage: Curve;
  /** Fatores do orçamento e do limite das ondas. */
  budget: number;
  limit: number;
  /** Chance de assombração reforçada: base + por dia, com teto. */
  elite: { base: number; perDay: number; max: number };
}

/** Parâmetros por dificuldade do Mutirão (doc 01, Parâmetros propostos). Hipóteses de playtest. */
export const DIFFICULTY_RULES: Record<Difficulty, DifficultyRules> = {
  tranquilo: { day: 330, night: 105, hp: [0.85, 0.1, 0.012], damage: [0.6, 0.08, 0.008], budget: 0.8, limit: 0.85, elite: { base: 0.02, perDay: 0.01, max: 0.15 } },
  arretado: { day: 300, night: 120, hp: [1, 0.12, 0.018], damage: [1, 0.1, 0.01], budget: 1, limit: 1, elite: { base: 0.03, perDay: 0.015, max: 0.25 } },
  sinistro: { day: 270, night: 135, hp: [1.2, 0.15, 0.025], damage: [1.3, 0.13, 0.015], budget: 1.25, limit: 1.15, elite: { base: 0.05, perDay: 0.02, max: 0.35 } },
};

/** Relógio (doc 01; doc 07, L6): Dia 0 mais longo, aviso de Boca da Noite e o ritmo da Treta. */
export const CLOCK = {
  /** O Dia 0 ganha isto, para a primeira bancada e o abrigo (só no Mutirão). */
  day0Bonus: 60,
  /** A Boca da Noite avisa isto antes do anoitecer. */
  dusk: 30,
  /** Treta: dia de 120 + 10·n s e noite de 60 s. */
  tretaDayBase: 120,
  tretaDayPerPlayer: 10,
  tretaNight: 60,
} as const;

/** A Treta não tem dificuldade: usa as curvas do Arretado. */
const rulesFor = (mode: Mode, difficulty: Difficulty): DifficultyRules => DIFFICULTY_RULES[mode === "treta" ? "arretado" : difficulty];

/** Duração do dia `day` e da noite que vem depois dele, em segundos. */
export function phaseDurations(mode: Mode, difficulty: Difficulty, players: number, day: number): { day: number; night: number } {
  if (mode === "treta") return { day: CLOCK.tretaDayBase + CLOCK.tretaDayPerPlayer * players, night: CLOCK.tretaNight };
  const r = DIFFICULTY_RULES[difficulty];
  return { day: r.day + (day === 0 ? CLOCK.day0Bonus : 0), night: r.night };
}

/** Dia que vale para curvas e pesos: na Treta, as noites pesam como o dobro (doc 01, Treta). */
export const effectiveDay = (mode: Mode, day: number): number => (mode === "treta" ? 2 * day : day);

export const hauntHpMul = (mode: Mode, difficulty: Difficulty, day: number): number => curve(rulesFor(mode, difficulty).hp, effectiveDay(mode, day));
export const hauntDamageMul = (mode: Mode, difficulty: Difficulty, day: number): number => curve(rulesFor(mode, difficulty).damage, effectiveDay(mode, day));

/** Ondas noturnas (doc 01, Ondas; doc 07, L1). */
export const WAVES = {
  /** Levas começam isto depois do anoitecer e param isto antes do amanhecer (s). */
  start: 5,
  stopBefore: 10,
  /** Assombrações nascem a esta distância do alvo da leva, longe de todo jogador, em grupos. */
  ring: [28, 45] as const,
  minPlayerDistance: 18,
  group: [1, 3] as const,
  /** Espalhamento do grupo em volta do ponto escolhido (m). */
  groupSpread: 4,
  attempts: 12,
  /** Teto absoluto de assombrações vivas (CPU do tick). */
  maxActive: 40,
  /** Ao amanhecer, cada uma se desfaz num instante sorteado até isto (s), sem loot. */
  dissolveWithin: 20,
  /** O orçamento entra nas levas dos primeiros 70 % da janela, para sobrar tempo à noite vazia (L1). */
  budgetWindow: 0.7,
  /** Noite vazia: 30 s sem assombrações e orçamento gasto devolvem 25 % (uma vez por noite). */
  emptyRefillAfter: 30,
  emptyRefillFraction: 0.25,
  /** Reforçada: vida, dano, escala, tostões e custo. */
  elite: { hp: 1.6, damage: 1.3, scale: 1.3, coins: 1.5, cost: 2 },
} as const;

/** Intervalo entre levas no dia `day` (s). */
export const waveInterval = (mode: Mode, day: number): number => Math.max(6, 12 - 0.6 * effectiveDay(mode, day));

/** Orçamento de Assombro da noite (pontos). */
export function nightBudget(mode: Mode, difficulty: Difficulty, day: number, players: number): number {
  if (mode === "treta") return 3 * players + 2 * day;
  return (8 + 4 * day) * (0.5 + 0.25 * players) * DIFFICULTY_RULES[difficulty].budget;
}

/** Assombrações vivas ao mesmo tempo, no máximo. */
export function nightLimit(mode: Mode, difficulty: Difficulty, day: number, players: number): number {
  if (mode === "treta") return Math.min(WAVES.maxActive, 2 + players);
  return Math.min(WAVES.maxActive, Math.floor((4 + 1.25 * day) * (0.6 + 0.2 * players) * DIFFICULTY_RULES[difficulty].limit));
}

/**
 * Peso de sorteio do tipo no dia: zero antes da entrada; depois sobe de 25 % até o peso máximo no
 * pico (o máximo é respeitado, ao contrário da referência).
 */
export function hauntWeight(type: CreatureType, day: number): number {
  const h = type.haunt;
  if (!h || day < h.startDay) return 0;
  const ramp = h.peakDay > h.startDay ? Math.min(1, (day - h.startDay) / (h.peakDay - h.startDay)) : 1;
  return h.maxWeight * (0.25 + 0.75 * ramp);
}

export function eliteChance(mode: Mode, difficulty: Difficulty, day: number): number {
  // Treta: sem reforçadas (doc 01).
  if (mode === "treta") return 0;
  const e = rulesFor(mode, difficulty).elite;
  return Math.min(e.max, e.base + e.perDay * effectiveDay(mode, day));
}

/** As seis assombrações, na ordem do catálogo. */
export const HAUNT_TYPES: readonly CreatureType[] = CREATURE_TYPES.filter((t) => t.haunt !== undefined);
